import type { StylePackChrome } from '@/themes/stylePack.types';
import { safeHref } from '@/utils/isAllowedUrl';

/**
 * Шапка квиза своими полями воронки (слово владельца 20.10.2026) — зеркало
 * серверной схемы `backend/src/ai-funnels/block-schema/quiz-header.ts`.
 * Совпадение перечней, границ и кода цвета в обе стороны держит сторож
 * `quiz-header.ends.spec.ts` (сервер читает этот файл с диска).
 *
 * «Перебивает пак»: поле задано — берётся оно; не задано — как решает пак, а
 * без пака — прежняя шапка. Ручной сейв кабинета идёт мимо схемы сервера,
 * поэтому разбор здесь — зажим отрисовки: негодное значение = нет поля.
 */
/**
 * Вид прогресса. `section-nodes` своим полем (слово владельца 21.10.2026,
 * Blinkist) — шаги-разделы шапки (`QuizSectionStepsProgress.tsx`), не узлы
 * пака с тем же именем. `section-bars` (задание stylepack/section-bars, Lovi)
 * — полоса, разбитая на отрезки по разделам; рисуют её те же шаги-разделы
 * плеера (`QuizSectionStepsProgress.tsx`).
 */
export const QUIZ_HEADER_PROGRESS_STYLES = [
  'bar',
  'segmented',
  'nodes',
  'section-nodes',
  'section-bars',
  'none',
] as const;
export const QUIZ_HEADER_BACK_BUTTONS = [
  'icon-bare',
  'icon-circle',
  'pill',
  'chevron',
  'circle-solid',
  'arrow',
  'none',
] as const;
export const QUIZ_HEADER_CLOSE_BUTTONS = [
  'none',
  'icon-bare',
  'icon-circle',
  'circle-solid',
] as const;
/**
 * Раскладка шапки: одной строкой, полоса под кнопками (нет поля — она) или
 * `logo-row` — «назад», логотип, счётчик одной строкой, полоса под ней
 * (слово владельца 21.10.2026).
 */
export const QUIZ_HEADER_LAYOUTS = [
  'one-row',
  'bar-below',
  'logo-row',
] as const;
/** Вид счётчика «N/M»: как есть или текущий номер цветом полосы. */
export const QUIZ_HEADER_COUNTER_STYLES = ['plain', 'accent-current'] as const;
/**
 * Разделитель счётчика: «12/30» (нет поля — он же), «12 / 30», «9 of 15» /
 * «9 из 15» — слово из словаря плеера (stylepack/header-counter-look).
 */
export const QUIZ_HEADER_COUNTER_SEPARATORS = [
  'slash',
  'slash-spaced',
  'word',
] as const;
/** Кнопка «вперёд»: перебивает пак; `none` — ни кнопки, ни её места. */
export const QUIZ_HEADER_FORWARD_BUTTONS = ['none', 'chevron'] as const;
/** Положение логотипа в шапке (слово владельца 21.10.2026); нет поля — как у пака. */
export const QUIZ_HEADER_LOGO_ALIGNS = ['left', 'center', 'right'] as const;
export const QUIZ_HEADER_BOUNDS = {
  progressThickness: { min: 1, max: 12 },
  logoHeight: { min: 12, max: 64 },
  counterFontSize: { min: 10, max: 24 },
  counterFontWeight: { min: 100, max: 900 },
  counterTotal: { min: 1, max: 500 },
  paddingTop: { min: 0, max: 80 },
  paddingX: { min: 0, max: 48 },
  rowGap: { min: 0, max: 48 },
  progressNodeSize: { min: 6, max: 24 },
  progressLabelFontSize: { min: 10, max: 18 },
  progressInsetRight: { min: 0, max: 120 },
  progressSegmentGap: { min: 0, max: 16 },
} as const;
/** Зазор между отрезками `section-bars` без поля, px (Lovi). */
export const QUIZ_HEADER_SEGMENT_GAP_DEFAULT = 4;
/** Свои пропорции отрезков `section-bars`: 2..8 целых весов 1..100. */
export const QUIZ_HEADER_SEGMENT_WEIGHTS = {
  minItems: 2,
  maxItems: 8,
  min: 1,
  max: 100,
} as const;
/** Узел без поля размера — прежние 10 px (`nodes`). */
export const QUIZ_HEADER_NODE_DEFAULT_SIZE = 10;
/** Кегль подписи раздела без поля, px. */
export const QUIZ_HEADER_LABEL_DEFAULT_SIZE = 14;
/** Линия между шагами-разделами без поля толщины, px. */
export const QUIZ_SECTION_STEPS_DEFAULT_THICKNESS = 3;
/** Потолок длины подписи раздела (поле экрана `progressSectionLabel`). */
export const QUIZ_SECTION_LABEL_MAX = 40;
/** Кегль и жирность счётчика без полей — прежние (12 px, обычная). */
export const QUIZ_HEADER_COUNTER_DEFAULT_SIZE = 12;
export const QUIZ_HEADER_COUNTER_DEFAULT_WEIGHT = 400;
/** Высота логотипа без поля — прежняя зашитая (WEB-1994). */
export const QUIZ_HEADER_LOGO_DEFAULT_HEIGHT = 24;
export const QUIZ_HEADER_CLOSE_URL_MAX = 2000;
/** Код цвета — то же правило, что BLOCK_COLOR_PATTERN сервера. */
export const QUIZ_HEADER_COLOR_PATTERN =
  /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export interface QuizHeaderSettings {
  progress?: (typeof QUIZ_HEADER_PROGRESS_STYLES)[number];
  progressColor?: string;
  progressTrackColor?: string;
  /** Заливка полосы градиентом слева направо; главнее progressColor. */
  progressGradient?: { from: string; to: string };
  progressThickness?: number;
  backButton?: (typeof QUIZ_HEADER_BACK_BUTTONS)[number];
  closeButton?: (typeof QUIZ_HEADER_CLOSE_BUTTONS)[number];
  closeUrl?: string;
  counter?: boolean;
  layout?: (typeof QUIZ_HEADER_LAYOUTS)[number];
  /** Цвет значка кнопок `circle-solid`, `chevron`, `arrow`; нет — прежний. */
  buttonIconColor?: string;
  logoAlign?: (typeof QUIZ_HEADER_LOGO_ALIGNS)[number];
  /** Высота логотипа, px; ширина — по пропорции картинки. */
  logoHeight?: number;
  counterStyle?: QuizCounterStyle;
  /** Кегль счётчика, px. */
  counterFontSize?: number;
  /** Жирность счётчика (font-weight). */
  counterFontWeight?: number;
  /** Явное «всего» в тексте счётчика; полоса и аналитика — по пути. */
  counterTotal?: number;
  /** Верх шапки от верха экрана, px (stylepack/header-insets). */
  paddingTop?: number;
  /** Боковые поля шапки от краёв экрана, px. */
  paddingX?: number;
  /** Промежуток от строки логотипа до ряда «назад + прогресс», px. */
  rowGap?: number;
  /** Цвет всего счётчика (stylepack/header-counter-look). */
  counterColor?: string;
  /** Цвет текущего номера; при accent-current главнее цвета полосы. */
  counterCurrentColor?: string;
  /** Цвет «всего» в счётчике. */
  counterTotalColor?: string;
  counterSeparator?: QuizCounterSeparator;
  /** Кнопка «вперёд» шапки; перебивает пак. */
  forwardButton?: (typeof QUIZ_HEADER_FORWARD_BUTTONS)[number];
  /** Размер узла у `nodes` и `section-nodes`, px. */
  progressNodeSize?: number;
  /** Кегль подписи текущего раздела у `section-nodes`, px. */
  progressLabelFontSize?: number;
  /** Конечный узел «финиш» у `section-nodes` после последнего раздела. */
  progressEndNode?: boolean;
  /** Отступ дорожки `section-nodes` и `section-bars` от правого края, px. */
  progressInsetRight?: number;
  /** Зазор между отрезками `section-bars`, px; нет — 4. */
  progressSegmentGap?: number;
  /** Свои пропорции отрезков `section-bars`; нет — отрезок на раздел. */
  progressSegmentWeights?: number[];
}

export type QuizCounterStyle = (typeof QUIZ_HEADER_COUNTER_STYLES)[number];
export type QuizCounterSeparator =
  (typeof QUIZ_HEADER_COUNTER_SEPARATORS)[number];

/** Цвета и разделитель счётчика «N/M»; нет цвета — прежний. */
export interface QuizCounterLook {
  color?: string;
  currentColor?: string;
  totalColor?: string;
  separator: QuizCounterSeparator;
}

const isOneOf = <T extends string>(list: readonly T[], v: unknown): v is T =>
  typeof v === 'string' && (list as readonly string[]).includes(v);
const isColor = (v: unknown): v is string =>
  typeof v === 'string' && QUIZ_HEADER_COLOR_PATTERN.test(v);
/** Целое в границах зеркала (`QUIZ_HEADER_BOUNDS`). */
const inBounds = (
  v: unknown,
  b: { readonly min: number; readonly max: number },
): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= b.min && v <= b.max;

/**
 * Список целых в границах (`QUIZ_HEADER_SEGMENT_WEIGHTS`) — одна дверь для
 * зажима и для ввода панели: годное — копия списка, прочее — `undefined`.
 */
export function intList(
  v: unknown,
  b: {
    readonly minItems: number;
    readonly maxItems: number;
    readonly min: number;
    readonly max: number;
  },
): number[] | undefined {
  if (!Array.isArray(v) || v.length < b.minItems || v.length > b.maxItems)
    return undefined;
  return v.every((x) => inBounds(x, b)) ? [...(v as number[])] : undefined;
}

/** Разбор сырого `settings.quizHeader`: годное поле остаётся, прочее выброшено. */
export function readQuizHeader(raw: unknown): QuizHeaderSettings {
  const out: QuizHeaderSettings = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  const r = raw as Record<string, unknown>;
  const { min, max } = QUIZ_HEADER_BOUNDS.progressThickness;
  const logoB = QUIZ_HEADER_BOUNDS.logoHeight;
  if (isOneOf(QUIZ_HEADER_PROGRESS_STYLES, r.progress))
    out.progress = r.progress;
  if (isColor(r.progressColor)) out.progressColor = r.progressColor;
  if (isColor(r.progressTrackColor))
    out.progressTrackColor = r.progressTrackColor;
  // Градиент — оба конца годным кодом цвета, иначе поля нет целиком.
  const g = r.progressGradient as Record<string, unknown> | null | undefined;
  if (g && typeof g === 'object' && isColor(g.from) && isColor(g.to)) {
    out.progressGradient = { from: g.from, to: g.to };
  }
  const t = r.progressThickness;
  if (typeof t === 'number' && Number.isInteger(t) && t >= min && t <= max) {
    out.progressThickness = t;
  }
  if (isOneOf(QUIZ_HEADER_BACK_BUTTONS, r.backButton))
    out.backButton = r.backButton;
  if (isOneOf(QUIZ_HEADER_CLOSE_BUTTONS, r.closeButton))
    out.closeButton = r.closeButton;
  const u = r.closeUrl;
  // Адрес — та же дверь, что у кнопки-ссылки (safeHref: негодное и пустое → '#').
  if (
    typeof u === 'string' &&
    u.length <= QUIZ_HEADER_CLOSE_URL_MAX &&
    safeHref(u) !== '#'
  ) {
    out.closeUrl = u;
  }
  if (typeof r.counter === 'boolean') out.counter = r.counter;
  if (isOneOf(QUIZ_HEADER_LAYOUTS, r.layout)) out.layout = r.layout;
  if (isColor(r.buttonIconColor)) out.buttonIconColor = r.buttonIconColor;
  if (isOneOf(QUIZ_HEADER_LOGO_ALIGNS, r.logoAlign))
    out.logoAlign = r.logoAlign;
  const lh = r.logoHeight;
  if (
    typeof lh === 'number' &&
    Number.isInteger(lh) &&
    lh >= logoB.min &&
    lh <= logoB.max
  ) {
    out.logoHeight = lh;
  }
  if (isOneOf(QUIZ_HEADER_COUNTER_STYLES, r.counterStyle))
    out.counterStyle = r.counterStyle;
  if (inBounds(r.counterFontSize, QUIZ_HEADER_BOUNDS.counterFontSize))
    out.counterFontSize = r.counterFontSize;
  if (inBounds(r.counterFontWeight, QUIZ_HEADER_BOUNDS.counterFontWeight))
    out.counterFontWeight = r.counterFontWeight;
  if (inBounds(r.counterTotal, QUIZ_HEADER_BOUNDS.counterTotal))
    out.counterTotal = r.counterTotal;
  if (inBounds(r.paddingTop, QUIZ_HEADER_BOUNDS.paddingTop))
    out.paddingTop = r.paddingTop;
  if (inBounds(r.paddingX, QUIZ_HEADER_BOUNDS.paddingX))
    out.paddingX = r.paddingX;
  if (inBounds(r.rowGap, QUIZ_HEADER_BOUNDS.rowGap)) out.rowGap = r.rowGap;
  if (isColor(r.counterColor)) out.counterColor = r.counterColor;
  if (isColor(r.counterCurrentColor))
    out.counterCurrentColor = r.counterCurrentColor;
  if (isColor(r.counterTotalColor)) out.counterTotalColor = r.counterTotalColor;
  if (isOneOf(QUIZ_HEADER_COUNTER_SEPARATORS, r.counterSeparator))
    out.counterSeparator = r.counterSeparator;
  if (isOneOf(QUIZ_HEADER_FORWARD_BUTTONS, r.forwardButton))
    out.forwardButton = r.forwardButton;
  if (inBounds(r.progressNodeSize, QUIZ_HEADER_BOUNDS.progressNodeSize))
    out.progressNodeSize = r.progressNodeSize;
  if (
    inBounds(r.progressLabelFontSize, QUIZ_HEADER_BOUNDS.progressLabelFontSize)
  )
    out.progressLabelFontSize = r.progressLabelFontSize;
  if (typeof r.progressEndNode === 'boolean')
    out.progressEndNode = r.progressEndNode;
  if (inBounds(r.progressInsetRight, QUIZ_HEADER_BOUNDS.progressInsetRight))
    out.progressInsetRight = r.progressInsetRight;
  if (inBounds(r.progressSegmentGap, QUIZ_HEADER_BOUNDS.progressSegmentGap))
    out.progressSegmentGap = r.progressSegmentGap;
  const weights = intList(
    r.progressSegmentWeights,
    QUIZ_HEADER_SEGMENT_WEIGHTS,
  );
  if (weights) out.progressSegmentWeights = weights;
  return out;
}

/**
 * Форма кнопки «назад» в плеере: формы пака и свои формы поля шапки
 * `circle-solid` (слово владельца 20.10.2026, дополнение: белый круг ~36 px
 * с тонкой тенью, внутри только стрелка — как у Mojo) и `arrow` (голая
 * стрелка «←», слово владельца 21.10.2026, Dance-bit). У паков их нет.
 */
export type QuizBackKind =
  StylePackChrome['backButton'] | 'circle-solid' | 'arrow';
/** Форма видимого крестика. */
export type QuizCloseKind = Exclude<
  (typeof QUIZ_HEADER_CLOSE_BUTTONS)[number],
  'none'
>;

export interface ResolvedQuizHeader {
  /**
   * 'legacy' — прежний текст «Шаг N из M» без пака; 'section-steps' — шаги-
   * разделы своим полем `progress: 'section-nodes'` (у пака `section-nodes`
   * — прежние узлы пака). `progress: 'section-bars'` — тоже 'section-steps'
   * с признаком `sectionBars`: плеер отдаёт шагам-разделам всю шапку, отрезки
   * рисует тот же модуль.
   */
  progressKind: StylePackChrome['progress'] | 'legacy' | 'section-steps';
  backKind: QuizBackKind;
  counter: boolean;
  /** Прежний текст «Шаг N из M» без пака: гасит только явное `counter: false`. */
  legacyCounter: boolean;
  /**
   * Сплошной цвет заливки (узлы, сегменты, номер счётчика): progressColor, а
   * при градиенте — его начало `from`.
   */
  fillColor?: string;
  /** Фон заливки полосы: градиент `linear-gradient(90deg, …)` или fillColor. */
  fillBackground?: string;
  trackColor?: string;
  thickness?: number;
  /** Размер узла (`nodes`, шаги-разделы); нет — прежний. */
  nodeSize?: number;
  /** Кегль подписи текущего раздела (шаги-разделы); нет — 14. */
  labelFontSize?: number;
  /** Конечный узел шагов-разделов; нет — узел на раздел. */
  endNode?: boolean;
  /** Отступ шагов-разделов от правого края строки, px; нет — во всю ширину. */
  insetRight?: number;
  /** Шаги-разделы рисуются отрезками полосы (`progress: 'section-bars'`). */
  sectionBars: boolean;
  /** Зазор между отрезками, px; нет поля — 4. */
  segmentGap: number;
  /** Свои пропорции отрезков; нет — отрезок на раздел. */
  segmentWeights?: number[];
  /** «назад», полоса и крестик одной строкой (поле layout = 'one-row'). */
  oneRow: boolean;
  /** «назад» — логотип — счётчик одной строкой, полоса под ней ('logo-row'). */
  logoRow: boolean;
  /** Вид счётчика; нет поля — 'plain' (как было). */
  counterStyle: QuizCounterStyle;
  counterFontSize?: number;
  counterFontWeight?: number;
  /**
   * Цвета и разделитель счётчика (stylepack/header-counter-look); без полей —
   * прежний вид «N/M» (разделитель `slash`, цветов нет).
   */
  counterLook: QuizCounterLook;
  /**
   * Явное «всего» в тексте счётчика (слово владельца 21.10.2026, Walk-fit);
   * нет — посчитанное по пути (quizHeaderScreen.ts, headerCounterTotal).
   */
  counterTotal?: number;
  /**
   * Свои отступы шапки (слово владельца 21.10.2026, Blinkist): верх и бока от
   * края экрана, промежуток под строкой логотипа; нет — как пак и плеер.
   * Стиль обёртки — quizHeaderInset.ts, промежуток — logoOwnRowStyle.
   */
  paddingTop?: number;
  paddingX?: number;
  rowGap?: number;
  /** Цвет значка кнопок circle-solid, chevron, arrow; нет — прежний. */
  iconColor?: string;
  /** Крестик «закрыть»: есть вид и годный адрес — иначе null. */
  close: { kind: QuizCloseKind; href: string } | null;
  /**
   * Логотип в шапке: положение и высота (нет высоты — прежние 24 px) или
   * null — в шапке логотипа нет (пак `none` / `desktop-left` без поля).
   * `own` — положение задано полем (а не паком). Наличие адреса и скрытие на
   * экране судит отрисовка.
   */
  logo: { align: QuizLogoAlign; height?: number; own: boolean } | null;
  /** Десктопный логотип вне шапки (пак `desktop-left`, поля положения нет). */
  desktopLogo: boolean;
}

export type QuizLogoAlign = (typeof QUIZ_HEADER_LOGO_ALIGNS)[number];

/** Положение логотипа без поля — по паку; без пака — по центру (WEB-922). */
function packLogoAlign(
  chrome: StylePackChrome | undefined,
): QuizLogoAlign | null {
  if (!chrome) return 'center';
  if (chrome.logo === 'left' || chrome.logo === 'center') return chrome.logo;
  return null;
}

/** Одна дверь «поле перебивает пак» для шапки плеера. */
export function resolveQuizHeader(
  chrome: StylePackChrome | undefined,
  raw: unknown,
): ResolvedQuizHeader {
  const h = readQuizHeader(raw);
  const closeKind = h.closeButton;
  const logoAlign = h.logoAlign ?? packLogoAlign(chrome);
  const grad = h.progressGradient;
  return {
    progressKind:
      h.progress === 'section-nodes' || h.progress === 'section-bars'
        ? 'section-steps'
        : (h.progress ?? (chrome ? chrome.progress : 'legacy')),
    backKind: h.backButton ?? (chrome ? chrome.backButton : 'icon-bare'),
    counter: h.counter ?? chrome?.counter ?? false,
    legacyCounter: h.counter ?? true,
    fillColor: grad ? grad.from : h.progressColor,
    fillBackground: grad
      ? `linear-gradient(90deg, ${grad.from}, ${grad.to})`
      : h.progressColor,
    trackColor: h.progressTrackColor,
    thickness: h.progressThickness,
    nodeSize: h.progressNodeSize,
    labelFontSize: h.progressLabelFontSize,
    endNode: h.progressEndNode,
    insetRight: h.progressInsetRight,
    sectionBars: h.progress === 'section-bars',
    segmentGap: h.progressSegmentGap ?? QUIZ_HEADER_SEGMENT_GAP_DEFAULT,
    segmentWeights: h.progressSegmentWeights,
    oneRow: h.layout === 'one-row',
    logoRow: h.layout === 'logo-row',
    counterStyle: h.counterStyle ?? 'plain',
    counterFontSize: h.counterFontSize,
    counterFontWeight: h.counterFontWeight,
    counterLook: {
      color: h.counterColor,
      currentColor: h.counterCurrentColor,
      totalColor: h.counterTotalColor,
      separator: h.counterSeparator ?? 'slash',
    },
    counterTotal: h.counterTotal,
    paddingTop: h.paddingTop,
    paddingX: h.paddingX,
    rowGap: h.rowGap,
    iconColor: h.buttonIconColor,
    close:
      h.closeUrl && closeKind && closeKind !== 'none'
        ? { kind: closeKind, href: safeHref(h.closeUrl) }
        : null,
    logo: logoAlign
      ? {
          align: logoAlign,
          height: h.logoHeight,
          own: h.logoAlign !== undefined,
        }
      : null,
    desktopLogo: chrome?.logo === 'desktop-left' && h.logoAlign === undefined,
  };
}

/**
 * Кнопка «вперёд» шапки: поле `forwardButton` перебивает пак
 * (`chrome.forwardButton`), нет поля — как у пака (stylepack/header-counter-look).
 * Одна дверь — её зовёт `useQuizChromeExtras`.
 */
export function quizHeaderForwardEnabled(
  chrome: StylePackChrome | undefined,
  raw: unknown,
): boolean {
  return (
    (readQuizHeader(raw).forwardButton ?? chrome?.forwardButton) === 'chevron'
  );
}

/**
 * Коробка узла по экранам (`nodes`) на дорожке толщиной `thickness`: без поля
 * размера — прежние 10 px и сдвиг (−4 сверху, −5 от точки), с полем — узел
 * своего размера по центру дорожки.
 */
export function progressNodeBox(
  nodeSize: number | undefined,
  thickness: number,
): { size: number; top: number; half: number } {
  const size = QUIZ_HEADER_NODE_DEFAULT_SIZE;
  if (nodeSize === undefined) return { size, top: -4, half: size / 2 };
  return {
    size: nodeSize,
    top: (thickness - nodeSize) / 2,
    half: nodeSize / 2,
  };
}

/**
 * Поля шапки, которым нужен ленивый кусок рамки (`quizChromeKit`): шеврон,
 * круг со стрелкой (`circle-solid`), голую стрелку (`arrow`) «назад»,
 * шеврон «вперёд», крестик, шаги-разделы (`progress: 'section-nodes'`) и полосу
 * по разделам (`section-bars`) рисует только он. Страница заказывает кусок и по ним, а не
 * только по паку (`usePlayerKitDoor`).
 */
export function quizHeaderNeedsChromeKit(raw: unknown): boolean {
  const h = readQuizHeader(raw);
  return (
    h.backButton === 'chevron' ||
    h.backButton === 'circle-solid' ||
    h.backButton === 'arrow' ||
    h.forwardButton === 'chevron' ||
    h.progress === 'section-nodes' ||
    h.progress === 'section-bars' ||
    resolveQuizHeader(undefined, h).close !== null
  );
}
